// Helpers for recovering from stale code-split chunks after a new deployment.
//
// When a new build is deployed, tabs still holding the old index.html will try
// to fetch code-split chunk file names (hashed) that no longer exist on the
// server and 404. React.lazy then throws a ChunkLoadError which, if uncaught,
// unmounts the whole app and leaves a BLANK PAGE. These helpers detect that
// specific failure and reload the tab once to pick up the fresh build.

const LAST_RELOAD_KEY = '__chunk_reload_ts__';
const RELOAD_COOLDOWN_MS = 60 * 1000; // at most one reload per 60s per tab

// True when the error is a webpack/React.lazy chunk-loading failure.
export const isChunkLoadError = (err) => {
    if (!err) return false;
    if (err.name === 'ChunkLoadError') return true;
    const message = typeof err === 'string' ? err : err.message;
    if (!message) return false;
    // e.g. "Loading chunk 12 failed." / "Loading CSS chunk 5 failed."
    return /Loading (CSS )?chunk [\w-]+ failed/i.test(message);
};

// Reload the page to fetch the new build, but at most ONCE per 60s per tab so a
// permanently broken server can't trigger an infinite reload loop. The timestamp
// lives in sessionStorage so it is scoped to this tab and cleared when it closes.
// Returns true if a reload was triggered.
export const reloadOnceForStaleChunk = () => {
    try {
        const last = Number(sessionStorage.getItem(LAST_RELOAD_KEY)) || 0;
        const now = Date.now();
        if (now - last < RELOAD_COOLDOWN_MS) {
            // Already reloaded very recently; the server is likely still broken.
            // Don't loop — let the error boundary show its fallback instead.
            return false;
        }
        sessionStorage.setItem(LAST_RELOAD_KEY, String(now));
    } catch (e) {
        // sessionStorage unavailable (private mode / quota) — fall through and
        // still attempt a single reload; the browser will de-dupe rapid loops
        // via the network cache.
    }
    window.location.reload();
    return true;
};
